import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Avatar, Button, Descriptions, Drawer, Empty, Form, Image, Modal, Select, Space, Spin, Table, Tabs, Tag, Tooltip } from "antd";
import MobileCardList from "@components/MobileCardList";
import {
  CheckOutlined,
  CloseOutlined,
  DeleteOutlined,
  RollbackOutlined,
  UserAddOutlined,
} from "@ant-design/icons";
import { useSelector } from "react-redux";
import moment from "moment";
import { api } from "@/redux/api";
import message from "@/utils/globalMessage";
import { getFacilityContext, buildFacilityPath } from "@/utils/route-context";
import {
  CONTRACT_STATUS_COLORS,
  getDisplayStatus,
  getDisplayStatusLabel,
} from "./constants";
import DeleteConfirm from "@components/Feedback/DeleteConfirm";
import PageHeader from "@components/Table/PageHeader";
import ResizableProTable from "@components/Table/ResizableProTable";
import ResponsiveSelect from "@components/ResponsiveSelect";
import {
  buildCertificateColumns,
  CERTIFICATE_TABLE_SCROLL_X,
  CERTIFICATE_TAB_TITLE,
  certificateRowKey,
} from "./certificateColumns";

const PENDING_STATUSES = new Set(["pending_invited", "pending_applied"]);
const ENGAGEMENT_STATUS_COLORS = {
  pending_invited: "gold",
  pending_applied: "processing",
  active: "success",
  rejected: "error",
  revoked: "default",
};

// Engagement status labels are side-dependent: the parenthesized direction is
// relative to the viewer (inbound = initiated by the other party, outbound =
// initiated by the viewer's own side).
const getEngagementStatusLabel = (value, isContractSide) => {
  switch (value) {
    case "pending_applied":
      return isContractSide ? "Request pending (outbound)" : "Request pending (inbound)";
    case "pending_invited":
      return isContractSide ? "Invitation pending (inbound)" : "Invitation pending (outbound)";
    case "active":
      return "Engaged";
    case "rejected":
      return "Rejected";
    case "revoked":
      return "Revoked";
    default:
      return value || "-";
  }
};

const getInitials = (value) => {
  const parts = String(value || "?").trim().split(/\s+/).filter(Boolean);
  return (parts.length > 1 ? `${parts[0][0]}${parts[parts.length - 1][0]}` : parts[0] || "?")
    .slice(0, 2)
    .toUpperCase();
};

const formatDate = (value) => (value ? moment(value).format("YYYY-MM-DD HH:mm") : "-");

// `other_service` is persisted as a comma-separated string; accept either that
// or an already-split array.
const splitOtherService = (value) => {
  if (Array.isArray(value)) return value.map((item) => String(item).trim()).filter(Boolean);
  return String(value || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);
};

// A value that has no content renders as a muted dash.
const renderValue = (value, fallback = "-") => {
  if (value === null || value === undefined || value === "") {
    return <span style={{ color: "#8c8c8c" }}>{fallback}</span>;
  }
  return value;
};

// An <a> that survives the modal's default click handling.
function ExternalLink({ href, children }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(event) => event.stopPropagation()}
    >
      {children}
    </a>
  );
}

const sortRelationships = (rows) => [...(rows || [])].sort((a, b) => {
  const pendingDelta = Number(PENDING_STATUSES.has(b.status)) - Number(PENDING_STATUSES.has(a.status));
  if (pendingDelta) return pendingDelta;
  return new Date(b.modified_date || b.created_date || 0) - new Date(a.modified_date || a.created_date || 0);
});

// Mirror the project's H5 detection (DetailTabs/Responsive components use the
// same rule): viewport <= 767px or the app explicitly flagged non-desktop.
function useIsH5(reduxIsPC) {
  const getViewportIsH5 = () =>
    typeof window !== "undefined" ? window.innerWidth <= 767 : reduxIsPC === false;
  const [isH5, setIsH5] = useState(() => reduxIsPC === false || getViewportIsH5());
  useEffect(() => {
    const onResize = () => setIsH5(reduxIsPC === false || getViewportIsH5());
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [reduxIsPC]);
  return isH5;
}

export default function ContractCompanyRelationshipTable({
  mode,
  loadRelationships,
  onCreate,
  onInvite,
  canWrite = false,
  canFullAccess = false,
  onAccept,
  onReject,
  onWithdraw,
  onApprove,
  onRemove,
  detailPath,
  title,
  companyIds = [],
  companyOptions = [],
  onCompanyChange,
}) {
  const isContractSide = mode === "contract";
  const isPC = useSelector((state) => state.App?.isPC ?? true);
  const context = useSelector((state) => getFacilityContext(state));
  const actionRef = useRef();
  const [detail, setDetail] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loadingRequests, setLoadingRequests] = useState(false);
  const isH5 = useIsH5(isPC);
  const [h5ActiveKey, setH5ActiveKey] = useState("overview");

  // When a different contractor's detail is opened (or closed), fall back to
  // the first section so the H5 dropdown never points at a stale tab.
  useEffect(() => {
    setH5ActiveKey("overview");
  }, [detail?.id]);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [deleting, setDeleting] = useState(false);
  const request = useCallback(async () => {
    try {
      const response = await loadRelationships({ company: companyIds });
      const payload = response?.data?.results || response?.data || response || [];
      return { data: sortRelationships(Array.isArray(payload) ? payload : []), total: Array.isArray(payload) ? payload.length : 0, success: true };
    } catch (_) {
      message.error("Failed to load company relationships");
      return { data: [], total: 0, success: false };
    }
  }, [companyIds, loadRelationships]);

  useEffect(() => {
    actionRef.current?.reload?.();
  }, [companyIds]);

  // Contract requests whose lifecycle is still in progress (a contractor is
  // actively bidding / awarded / completing) rank before terminal ones.
  const STATUS_SORT_RANK = {
    sent: 0,
    bidding: 1,
    awarded: 2,
    accepted: 3,
    pending_acceptance: 4,
    not_awarded: 5,
    closed: 6,
    cancelled: 7,
  };
  const sortContractRequests = (rows) =>
    [...(rows || [])].sort((a, b) => {
      const rankDelta =
        (STATUS_SORT_RANK[getDisplayStatus(a)] ?? 99) -
        (STATUS_SORT_RANK[getDisplayStatus(b)] ?? 99);
      if (rankDelta) return rankDelta;
      return new Date(b.created_date || 0) - new Date(a.created_date || 0);
    });

  // Load every contract request linked to the opened row whenever a detail
  // drawer/modal is opened.
  //  - Facility/portfolio side ("contractor" detail): every contract request of
  //    the current facility that this contractor company received (awarded or quoted).
  //  - Contractor "Clients" side (client company detail): every contract request
  //    of that client company that involves the current contractor.
  const detailContractorId = detail?.contract_company_id || detail?.contract_company;
  const detailCompanyId = detail?.company_id || detail?.company;
  useEffect(() => {
    if (!detail) {
      setRequests([]);
      return;
    }
    let active = true;
    setLoadingRequests(true);
    const load = isContractSide
      ? detailCompanyId
        ? api.getContractRequests(undefined, {
            company: [detailCompanyId],
            page_size: 200,
          })
        : Promise.resolve({ data: [] })
      : (() => {
          const contractorId = detailContractorId;
          const facilityId = context?.facilityId;
          return contractorId && facilityId
            ? api.getContracts([facilityId], {
                contract_company: contractorId,
                page_size: 200,
              })
            : Promise.resolve({ data: [] });
        })();
    load
      .then((response) => {
        if (!active) return;
        const payload = response?.data?.results || response?.data || [];
        setRequests(sortContractRequests(Array.isArray(payload) ? payload : []));
      })
      .catch(() => {
        if (active) setRequests([]);
      })
      .finally(() => {
        if (active) setLoadingRequests(false);
      });
    return () => {
      active = false;
    };
  }, [detail, detailCompanyId, detailContractorId, context?.facilityId, isContractSide]);

  const refresh = useCallback(async (operation) => {
    try {
      await operation();
      actionRef.current?.reload?.();
    } catch (error) {
      message.error(error?.response?.data?.detail || Object.values(error?.response?.data || {})[0] || "Unable to update relationship");
    }
  }, []);

  const canRemove = Boolean(onRemove && (isContractSide || canFullAccess));
  const handleSelectionChange = useCallback((keys, rows) => {
    setSelectedRowKeys(keys);
    setSelectedRows((previousRows) => {
      const rowMap = new Map();
      [...previousRows, ...(rows || [])].forEach((row) => {
        if (row?.id != null) rowMap.set(row.id, row);
      });
      return keys.map((key) => rowMap.get(key)).filter(Boolean);
    });
  }, []);

  const clearSelection = useCallback(() => {
    setSelectedRowKeys([]);
    setSelectedRows([]);
  }, []);

  const handleBulkDelete = useCallback(async () => {
    if (!canRemove || !selectedRowKeys.length) return;
    try {
      setDeleting(true);
      const rows = selectedRows.length ? selectedRows : selectedRowKeys.map((id) => ({ id }));
      await Promise.all(rows.map((row) => onRemove(row.id)));
      message.success(isContractSide ? "Disengaged successfully" : "Deleted successfully");
      clearSelection();
      actionRef.current?.reload?.();
    } catch (error) {
      message.error(error?.response?.data?.detail || "Unable to delete selected relationships");
    } finally {
      setDeleting(false);
    }
  }, [canRemove, clearSelection, onRemove, selectedRowKeys, selectedRows]);

  const renderBulkActionsBar = useCallback(() => {
    if (!canRemove || !selectedRowKeys.length) return null;
    return (
      <div className="table-bulk-floating-bar" role="toolbar" aria-label="Bulk actions for selected rows">
        <Space size={10} align="center" wrap className="table-bulk-floating-bar-inner">
          <span className="table-bulk-floating-bar-count">
            <span className="table-bulk-floating-bar-count-label">Selected</span>
            <span className="table-bulk-floating-bar-count-num">{selectedRowKeys.length}</span>
            <Tooltip title="Clear selection">
              <Button
                type="text"
                size="small"
                className="table-bulk-floating-bar-clear"
                icon={<CloseOutlined />}
                onClick={clearSelection}
                aria-label="Clear selection"
              />
            </Tooltip>
          </span>
          <Tooltip title={isContractSide ? "Disengage selected" : "Delete selected"}>
            <DeleteConfirm
              title={isContractSide ? "Disengage the selected relationships?" : "Delete the selected relationships?"}
              description="This action cannot be undone!"
              onConfirm={handleBulkDelete}
              zIndex={1500}
            >
              <Button
                type="default"
                danger
                shape="circle"
                className="table-bulk-floating-btn"
                icon={<DeleteOutlined />}
                loading={deleting}
                aria-label={isContractSide ? "Disengage selected" : "Delete selected"}
              />
            </DeleteConfirm>
          </Tooltip>
        </Space>
      </div>
    );
  }, [canRemove, clearSelection, deleting, handleBulkDelete, isContractSide, selectedRowKeys.length]);

  // Columns shared by the desktop Table and the H5 MobileCardList in the
  // Contract Requests section.
  const requestColumns = useMemo(
    () => [
      {
        title: "Title",
        dataIndex: "title",
        key: "title",
        ellipsis: true,
        render: (value, item) => {
          const requestPath = isContractSide
            ? `/contract/contract-request-detail/${item.id}`
            : buildFacilityPath(
                `contract-request-detail/${item.id}`,
                { companyId: context.companyId, facilityId: context.facilityId }
              );
          return (
            <a
              href={requestPath || "#"}
              onClick={(event) => {
                event.preventDefault();
                if (requestPath) window.location.href = requestPath;
              }}
            >
              {value || `Contract Request #${item.id}`}
            </a>
          );
        },
      },
      {
        title: "Status",
        key: "status",
        width: 150,
        render: (_, item) => (
          <Tag color={CONTRACT_STATUS_COLORS[getDisplayStatus(item)] || "default"}>
            {getDisplayStatusLabel(item)}
          </Tag>
        ),
      },
      {
        title: "Due",
        dataIndex: "due_date",
        key: "due_date",
        width: 120,
        render: (value) => (value ? moment(value).format("YYYY-MM-DD") : "-"),
      },
      {
        title: "Created",
        dataIndex: "created_date",
        key: "created_date",
        width: 150,
        render: formatDate,
      },
    ],
    [context.companyId, context.facilityId, isContractSide]
  );

  // Columns for the "Licenses & Certificates" section (company-owned records).
  const certificateColumns = useMemo(() => buildCertificateColumns(), []);

  // The content panels are shared by the desktop <Tabs> and the H5
  // dropdown-driven panel, so the two viewports stay visually consistent.
  // NOTE: `detail` is null whenever the modal/drawer is closed, and this memo
  // runs on every render — so every field access must be null-safe here.
  const detailSections = useMemo(() => {
    if (!detail) return [];

    // Shared Contract Requests section: lists every contract request tied to the
    // opened record. On the contractor "Clients" side the base contract-request
    // queryset is already scoped to the current contractor (self), so filtering
    // by the client company alone yields exactly the requests shared between the
    // two parties. On the facility side it is shown for a contractor detail.
    const contractRequestsSection = {
      key: "contract-requests",
      label: `Contract Requests${requests.length ? ` (${requests.length})` : ""}`,
      render: () => (
        <Spin spinning={loadingRequests}>
          {isH5 ? (
            <MobileCardList
              size="small"
              rowKey="id"
              columns={requestColumns}
              dataSource={requests}
              loading={loadingRequests}
              pagination={{ defaultPageSize: 20 }}
              locale={{ emptyText: "No contract requests" }}
            />
          ) : (
            <Table
              size="small"
              rowKey="id"
              dataSource={requests}
              scroll={{ x: 700 }}
              pagination={{
                defaultPageSize: 20,
                showSizeChanger: true,
                pageSizeOptions: ["20", "50", "100"],
                showTotal: (total) => `${total} requests`,
              }}
              locale={{ emptyText: "No contract requests" }}
              columns={requestColumns}
            />
          )}
        </Spin>
      ),
    };

    // Contractor "Clients" view keeps a minimal company card; the facility side
    // shows the contractor's full profile. The client company's contact info is
    // masked by the backend until the relationship becomes active.
    if (isContractSide) {
      const sections = [
        {
          key: "overview",
          label: "Overview",
          render: () => (
            <Descriptions column={1} size="middle" bordered>
              <Descriptions.Item label="Name">
                <Space>
                  <Avatar src={detail.company_logo}>{getInitials(detail.company_name)}</Avatar>
                  {detail.company_name || "-"}
                </Space>
              </Descriptions.Item>
            </Descriptions>
          ),
        },
        {
          key: "contact",
          label: "Contact",
          render: () => (
            <Descriptions column={1} size="middle" bordered>
              <Descriptions.Item label="Contact name">
                {renderValue(detail.company_contact_name)}
              </Descriptions.Item>
              <Descriptions.Item label="Phone">
                {renderValue(detail.company_contact_phone)}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {renderValue(detail.company_contact_email)}
              </Descriptions.Item>
            </Descriptions>
          ),
        },
      ];
      if (detailCompanyId) {
        sections.push(contractRequestsSection);
      }
      return sections;
    }

    const mainServices = (detail.service_categories || []).filter((item) => item && item.name);
    const otherServices = splitOtherService(
      detail.contract_company_other_service ?? detail.other_service
    );
    const certificates = Array.isArray(detail.certificates) ? detail.certificates.filter(Boolean) : [];

    const sections = [
      {
        key: "overview",
        label: "Overview",
        render: () => (
          <Descriptions column={1} size="middle" bordered>
            <Descriptions.Item label="Name">
              <Space>
                <Avatar src={detail.contract_company_logo}>{getInitials(detail.contract_company_name)}</Avatar>
                {detail.contract_company_name || "-"}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label="Login email">
              {renderValue(detail.contract_company_email || detail.company_email)}
            </Descriptions.Item>
            <Descriptions.Item label="Address">
              {renderValue(detail.contract_company_address || detail.address)}
            </Descriptions.Item>
            <Descriptions.Item label="Description">
              {renderValue(detail.contract_company_description || detail.description)}
            </Descriptions.Item>
            <Descriptions.Item label="Main service">
              {mainServices.length ? (
                <Space wrap size={[8, 10]}>
                  {mainServices.map((item) => (
                    <Tag key={item.id || item.name} color="blue">{item.name}</Tag>
                  ))}
                </Space>
              ) : (
                <span style={{ color: "#8c8c8c" }}>No main service</span>
              )}
            </Descriptions.Item>
            <Descriptions.Item label="Other service">
              {otherServices.length ? (
                <Space wrap size={[8, 10]}>
                  {otherServices.map((item) => (
                    <Tag key={item}>{item}</Tag>
                  ))}
                </Space>
              ) : (
                <span style={{ color: "#8c8c8c" }}>No other service</span>
              )}
            </Descriptions.Item>
          </Descriptions>
        ),
      },
      {
        key: "certificates",
        label: CERTIFICATE_TAB_TITLE(certificates.length),
        render: () =>
          certificates.length ? (
            isH5 ? (
              <MobileCardList
                size="small"
                rowKey={certificateRowKey}
                columns={certificateColumns}
                dataSource={certificates}
                pagination={false}
                locale={{ emptyText: "No licenses or certificates" }}
              />
            ) : (
              <Table
                size="small"
                rowKey={certificateRowKey}
                dataSource={certificates}
                scroll={{ x: CERTIFICATE_TABLE_SCROLL_X }}
                pagination={false}
                locale={{ emptyText: "No licenses or certificates" }}
                columns={certificateColumns}
              />
            )
          ) : (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No licenses or certificates"
              style={{ marginTop: 24 }}
            />
          ),
      },
      {
        key: "contact",
        label: "Contact",
        render: () => (
          <Descriptions column={1} size="middle" bordered>
            <Descriptions.Item label="Contact name">
              {renderValue(detail.contract_company_contact_name || detail.contact_name)}
            </Descriptions.Item>
            <Descriptions.Item label="Phone">
              {renderValue(detail.contract_company_contact_phone || detail.contact_phone)}
            </Descriptions.Item>
            <Descriptions.Item label="Email">
              {detail.contract_company_contact_email || detail.contact_email ? (
                <ExternalLink
                  href={`mailto:${detail.contract_company_contact_email || detail.contact_email}`}
                >
                  {detail.contract_company_contact_email || detail.contact_email}
                </ExternalLink>
              ) : (
                <span style={{ color: "#8c8c8c" }}>-</span>
              )}
            </Descriptions.Item>
          </Descriptions>
        ),
      },
    ];
    if (detailContractorId) {
      sections.push(contractRequestsSection);
    }
    return sections;
  }, [
    certificateColumns,
    detailCompanyId,
    detailContractorId,
    isH5,
    isContractSide,
    loadingRequests,
    requestColumns,
    requests,
    detail,
  ]);

  const renderSectionSwitch = (key) => detailSections.find((s) => s.key === key)?.render?.();

  const detailContent = detail
    ? isH5
      ? (
        <div>
          {detailSections.length > 1 ? (
            <Select
              style={{ width: "100%", marginBottom: 16 }}
              value={h5ActiveKey}
              onChange={setH5ActiveKey}
              options={detailSections.map((s) => ({ value: s.key, label: s.label }))}
            />
          ) : null}
          {renderSectionSwitch(h5ActiveKey)}
        </div>
      )
      : (
        <Tabs defaultActiveKey="overview" destroyInactiveTabPane>
          {detailSections.map((s) => (
            <Tabs.TabPane tab={s.label} key={s.key}>
              {s.render()}
            </Tabs.TabPane>
          ))}
        </Tabs>
      )
    : null;

  const openDetail = useCallback((record) => {
    if (detailPath) {
      detailPath(record);
      return;
    }
    setDetail(record);
  }, [detailPath]);

  const columns = useMemo(() => {
    const nameKey = isContractSide ? "company_name" : "contract_company_name";
    const logoKey = isContractSide ? "company_logo" : "contract_company_logo";
    return [
      {
        title: isContractSide ? "Client Name" : "Contractor",
        dataIndex: nameKey,
        key: nameKey,
        width: 260,
        render: (value, record) => (
          <a
            href="#details"
            onClick={(event) => { event.preventDefault(); openDetail(record); }}
            style={{ color: "#1677ff", textDecoration: "underline" }}
          >
            <Space size={8}>
              <Avatar size={30} src={record[logoKey]}>{getInitials(value)}</Avatar>
              <span>{value || "-"}</span>
            </Space>
          </a>
        ),
      },
      ...(isContractSide
        ? []
        : [
          {
            title: "Email",
            dataIndex: "contract_company_email",
            key: "contract_company_email",
            width: 230,
            render: (value, record) => value || record.company_email || "-",
          },
          {
            title: "Service categories",
            key: "service_categories",
            width: 240,
            render: (_, record) => {
              const categories = (record.service_categories || []).filter((item) => item?.name);
              if (!categories.length) return "-";
              const categoryNames = categories.map((item) => item.name);
              return (
                <Tooltip title={categoryNames.join(", ")}>
                  <Space size={[4, 4]} wrap>
                    {categories.map((item) => <Tag key={item.id || item.name} color="blue">{item.name}</Tag>)}
                  </Space>
                </Tooltip>
              );
            },
          },
          {
            title: "Status",
            dataIndex: "contract_company_is_active",
            key: "contract_company_is_active",
            width: 110,
            render: (value) =>
              value ? <Tag color="success">Active</Tag> : <Tag color="default">Disabled</Tag>,
          },
        ]),
      {
        title: "Engagement Status",
        dataIndex: "status",
        key: "engagement_status",
        width: 210,
        render: (value) => (
          <Tag color={ENGAGEMENT_STATUS_COLORS[value]}>{getEngagementStatusLabel(value, isContractSide)}</Tag>
        ),
      },
      {
        title: "Last updated",
        dataIndex: "modified_date",
        key: "modified_date",
        width: 170,
        sorter: true,
        render: formatDate,
      },
      {
        title: "Actions",
        key: "actions",
        valueType: "option",
        fixed: "right",
        width: 220,
        render: (_, record) => (
          <Space size={4} wrap>
            {isContractSide && record.status === "pending_invited" ? <Button size="small" type="primary" icon={<CheckOutlined />} onClick={() => refresh(() => onAccept(record.id))}>Accept</Button> : null}
            {isContractSide && record.status === "pending_invited" ? <Button size="small" icon={<CloseOutlined />} onClick={() => refresh(() => onReject(record.id))}>Reject</Button> : null}
            {isContractSide && record.status === "pending_applied" ? <Button size="small" icon={<RollbackOutlined />} onClick={() => refresh(() => onWithdraw(record.id))}>Withdraw</Button> : null}
            {!isContractSide && record.status === "pending_applied" && canWrite ? <Button size="small" type="primary" icon={<CheckOutlined />} onClick={() => refresh(() => onApprove(record.id))}>Approve</Button> : null}
            {!isContractSide && record.status === "pending_applied" && canWrite ? <Button size="small" icon={<CloseOutlined />} onClick={() => refresh(() => onReject(record.id))}>Reject</Button> : null}
            {record.status !== "revoked" && canRemove ? (
              <DeleteConfirm
                title={isContractSide ? "Disengage from this company?" : "Disengage this contractor?"}
                description="This action cannot be undone!"
                okText="Disengage"
                onConfirm={() => refresh(() => onRemove(record.id))}
                zIndex={1500}
              >
                <Button danger size="small" icon={<DeleteOutlined />}>Disengage</Button>
              </DeleteConfirm>
            ) : null}
          </Space>
        ),
      },
    ];
  }, [canFullAccess, canRemove, canWrite, isContractSide, onAccept, onApprove, onReject, onRemove, onWithdraw, openDetail, refresh]);

  const toolbar = (
    <PageHeader
      hide_imports
      hide_search
      hid_new
      handleFresh={() => actionRef.current?.reload?.()}
      renderSearches={() => companyOptions.length ? (
        <Form className="filter-form contract-filter-form" autoComplete="off">
          <Form.Item label="Company" name="company">
            <ResponsiveSelect
              mode="multiple"
              allowClear
              showSearch
              popupTitle="Select Companies"
              placeholder="Company"
              value={companyIds}
              options={companyOptions.map((item) => ({ value: item.id, label: item.name }))}
              onChange={(value) => onCompanyChange?.(value || [])}
            />
          </Form.Item>
        </Form>
      ) : null}
      resetFilters={() => onCompanyChange?.([])}
      filterHasValue={Boolean(companyIds.length)}
      renderButtons={() => (
        <Space size={8}>
          {isContractSide ? (
            onCreate ? (
              <Tooltip title="Request to connect client">
                <Button type="primary" shape={isPC ? "default" : "circle"} icon={<UserAddOutlined />} onClick={() => onCreate(() => actionRef.current?.reload?.())} aria-label="Request to connect client">
                  {isPC ? "Request to connect client" : null}
                </Button>
              </Tooltip>
            ) : null
          ) : onInvite ? (
            <Button type="primary" shape={isPC ? "default" : "circle"} icon={<UserAddOutlined />} onClick={() => onInvite(() => actionRef.current?.reload?.())} aria-label="Invite contractor">
              {isPC ? "Invite contractor" : null}
            </Button>
          ) : null}
        </Space>
      )}
    />
  );

  return (
    <div style={{ width: "100%", minWidth: 0, padding: isPC ? 16 : 8 }}>
      <ResizableProTable
        actionRef={actionRef}
        headerTitle={title}
        rowKey="id"
        columns={columns}
        request={request}
        search={false}
        options={false}
        rowSelection={{ selectedRowKeys, onChange: handleSelectionChange, preserveSelectedRowKeys: true }}
        pagination={{ defaultPageSize: 20, showSizeChanger: true }}
        scroll={{ x: isContractSide ? 910 : 1050 }}
        toolBarRender={() => toolbar}
        mobileTitleOnClick={openDetail}
        locale={{ emptyText: isContractSide ? "No company invitations or applications" : "No contractor relationships" }}
      />
      {renderBulkActionsBar()}
      {isPC ? (
        <Modal open={Boolean(detail)} title={detail ? (isContractSide ? detail.company_name : detail.contract_company_name) : "Details"} onCancel={() => setDetail(null)} footer={null} width="80%" bodyStyle={{ paddingTop: 8 }}>
          {detailContent}
        </Modal>
      ) : (
        <Drawer
          placement="bottom"
          height="100vh"
          visible={Boolean(detail)}
          title={detail ? (isContractSide ? detail.company_name : detail.contract_company_name) : "Details"}
          onClose={() => setDetail(null)}
          destroyOnClose={false}
          className="contract-relationship-detail-drawer"
          bodyStyle={{ paddingTop: 12, overflowY: "auto" }}
        >
          {detailContent}
        </Drawer>
      )}
    </div>
  );
}
